import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../../redux/slices/authSlice";
import getAPIbaseURL from "../../../utils/helpers";
import { showToast } from "../../../redux/slices/toastSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Login.css";
import { FaEye, FaEyeSlash, FaRegEnvelope, FaLock, FaBuilding } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getCurrentPosition } from "../../../utils/workLocation";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { t, i18n } = useTranslation(); 
  
  const { isLoading, error } = useSelector((state) => state.auth);

  useEffect(() => {
    const codigoErro = searchParams.get("sso_error");
    if (codigoErro) {
      const translationKey = `slices.authSlice.errors.${codigoErro.toLowerCase()}`;
      const mensagem = i18n.exists(translationKey)
        ? t(translationKey)
        : t("slices.authSlice.errors.generic_error");

      dispatch(showToast({ message: mensagem, type: "error" }));

      searchParams.delete("sso_error");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(login({ email, password })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        // Asks for the location right away, so the first clock-in does not wait for it
        getCurrentPosition();
        navigate("/dashboard");
      }
    });
  };

  const handleSSOLogin = () => {
  window.location.href = `${getAPIbaseURL()}/auth/sso/initiate`;
};

  return (
    <div className="login-container">
      <div className="bg-blob blob-1" aria-hidden="true"></div>
      <div className="bg-blob blob-2" aria-hidden="true"></div>
      <div className="bg-blob blob-3" aria-hidden="true"></div>

      <div className="login-card-wrapper atlar-card">
        <div className="text-center">
          <h1 className="logo-title">
            Agility<span>HR</span>
          </h1>

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3 text-start field-group">
              <FaRegEnvelope className="field-icon" />
              <Form.Control
                type="email"
                placeholder={t("pages.Login.email_placeholder")}
                className="form-control-custom"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                aria-label={t("pages.Login.email_placeholder")}
                required
              />
            </Form.Group>

            <Form.Group className="mb-2 position-relative text-start field-group">
              <FaLock className="field-icon" />
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder={t("pages.Login.password_placeholder")}
                className="form-control-custom"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                aria-label={t("pages.Login.password_placeholder")}
                required
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                role="button"
                tabIndex={0}
                aria-label={showPassword ? t("pages.Loginhide_password") : t("pages.Login.show_password")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setShowPassword(!showPassword);
                }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </Form.Group>

            {error && (
              <p className="error-text" role="alert">
                {error?.detail || error?.message || t("pages.Login.default_error")}
              </p>
            )}

            <Button type="submit" className="btn-login" disabled={isLoading}>
              {isLoading && <span className="btn-spinner" aria-hidden="true"></span>}
              {isLoading ? t("pages.Login.logging_in") : t("pages.Login.login_button")}
            </Button>

            <a href="/forgot-password" className="forgot-password d-block mt-3 mb-1">
              {t("pages.Login.forgot_password")}
            </a>
          </Form>

          <div className="position-relative my-4 text-center">
            <hr className="position-absolute top-50 w-100 my-0 translate-middle-y z-0" style={{ opacity: 0.2 }} />
            <span className="px-3 bg-surface txt-subtle small position-relative z-1" style={{ borderRadius: "10px" }}>
              {t("pages.Login.or")}
            </span>
          </div>

          <Button 
            variant="outline-primary" 
            className="w-100 py-2 d-flex align-items-center justify-content-center gap-2" 
            onClick={handleSSOLogin}
            disabled={isLoading}
            style={{ fontWeight: "600", borderRadius: "8px", border: "1px solid var(--primary-color, #0d6efd)" }}
          >

              <FaBuilding />
              {t("pages.Login.sso_button")}
          </Button>

        </div>
      </div>

      <div className="footer-text">
        <a href="https://www.atlar.pt" target="_blank" rel="noopener noreferrer">
          Powered by Atlar
        </a>
      </div>
    </div>
  );
};

export default LoginPage;